import { useEffect, useCallback, useRef } from 'react';

const TAB_DIRECTION = {
  FORWARD: 'forward',
  BACKWARD: 'backward'
};

/**
 * Custom hook to prevent Material-UI Modal container from getting focus during tab navigation
 * and redirect focus to the appropriate form element inside the modal.
 */
const useDialogFocusHandler = ({
  isOpen,
  containerQuerySelector,
  trapFocusWithDatePicker = false
}) => {
  const lastTabDirectionRef = useRef(TAB_DIRECTION.FORWARD);

  const handleKeyDown = useCallback(e => {
    if (e.key === 'Tab') {
      lastTabDirectionRef.current = e.shiftKey
        ? TAB_DIRECTION.BACKWARD
        : TAB_DIRECTION.FORWARD;
    }
  }, []);

  const handleDialogFocus = useCallback(e => {
    e.preventDefault();
    const dialogContainer = e.currentTarget;
    const focusableElements = dialogContainer.querySelectorAll(
      'a[href]:not(:disabled), input:not(:disabled):not([tabindex="-1"]), button:not(:disabled):not([tabindex="-1"]), select:not(:disabled):not([tabindex="-1"]), textarea:not(:disabled):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      if (lastTabDirectionRef.current === TAB_DIRECTION.BACKWARD) {
        focusableElements[focusableElements.length - 1].focus();
      } else {
        focusableElements[0].focus();
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const dialogContainer = document.querySelector(containerQuerySelector);

        if (dialogContainer) {
          dialogContainer.addEventListener('keydown', handleKeyDown);
          dialogContainer.addEventListener('focus', handleDialogFocus);
        }
      }, 0);

      return () => {
        clearTimeout(timer);

        const dialogContainer = document.querySelector(containerQuerySelector);

        if (dialogContainer) {
          dialogContainer.removeEventListener('keydown', handleKeyDown);
          dialogContainer.removeEventListener('focus', handleDialogFocus);
        }
      };
    }

    return null;
  }, [isOpen, containerQuerySelector, handleKeyDown, handleDialogFocus]);

  /**
     Custom focus containment for MUI v5 DatePicker support.
     MUI v5 DatePicker Popper does not receive focus when used inside MUI v4 Dialog/Drawer due to v4's enforceFocus behavior.
     We disable enforceFocus and implement a custom focus trap that allows the DatePicker Popper to receive focus while maintaining focus containment.
   */
  useEffect(() => {
    if (!isOpen || !trapFocusWithDatePicker || !containerQuerySelector) {
      return undefined;
    }

    const handleFocusContainment = () => {
      const modalContainer = document.querySelector(containerQuerySelector);

      if (!modalContainer) {
        return;
      }

      const { activeElement } = document;

      const isInThisModal = modalContainer.contains(activeElement);

      if (isInThisModal) return;

      const isInDatePickerPopper = document
        .querySelector('[role="dialog"][class*="MuiPickersPopper"]')
        ?.contains(activeElement);

      if (isInDatePickerPopper) return;

      const isInChildDialog = Array.from(
        document.querySelectorAll('.MuiDialog-container')
      ).some(
        container =>
          container !== modalContainer && container.contains(activeElement)
      );

      if (isInChildDialog) return;

      // Exclude TrapFocus sentinel elements from portal detection.
      // Sentinels are invisible divs (tabIndex="0") that MUI's TrapFocus uses to create
      // keyboard navigation loops. They're part of the modal's internal focus management,
      // not external portals, and must be excluded to prevent focus escape during Tab navigation.
      const isSentinelElement =
        activeElement.getAttribute('data-test') === 'sentinelStart' ||
        activeElement.getAttribute('data-test') === 'sentinelEnd';

      // Check if focus is in any portal element (Popper or Popover based components)
      const isInPortal =
        !isSentinelElement &&
        Array.from(
          document.querySelectorAll('[class*="Popper"], [class*="Popover"]')
        ).some(el => el.contains(activeElement));

      if (isInPortal) return;

      modalContainer.focus();
    };

    document.addEventListener('focus', handleFocusContainment, true);

    return () => {
      document.removeEventListener('focus', handleFocusContainment, true);
    };
  }, [isOpen, containerQuerySelector, trapFocusWithDatePicker]);
};

export default useDialogFocusHandler;
